// JobsSection — Phase 4A + Phase 10 R2
// Renders the ticket's jobs inside the Parts & Labor tab.
//
// Authority contract: this component DISPLAYS jobs only. Mutations (Add Job)
// reuse the existing AddJobDialog path verbatim — ticket_jobs remains the
// sole time-bearing scheduler authority. No new time model is introduced here.
//
// Phase 10 R2: When labor_engine_enabled && ui_ticket_financials are ON, each
// job row also renders a per-row sell-side labor strip (display + override).
// This is strictly sell-side — never feeds the scheduler / pay / cost paths.

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Wrench, Clock, Timer, Package2, Loader2 } from 'lucide-react';
import { AddJobDialog } from '@/components/tickets/AddJobDialog';
import { AddLaborDialog } from './AddLaborDialog';
import { JobLaborStrip } from './JobLaborStrip';
import { useFeatureFlag, useExposureFlag } from '@/hooks/useFeatureFlags';
import { useTicketLaborLines } from '@/hooks/labor/useTicketLaborLines';
import { useComputeJobLabor } from '@/hooks/labor/useComputeJobLabor';
import { useTicketTechPayLines } from '@/hooks/labor/useTicketTechPayLines';
import { useComputeJobTechPay } from '@/hooks/labor/useComputeJobTechPay';
import { JobTechPayStrip } from './JobTechPayStrip';
import { useUserRole } from '@/hooks/useUserRole';

export interface JobsSectionJob {
  id: string;
  name: string;
  standardMinutes: number;
  category?: string;
}

interface JobsSectionProps {
  ticketId: string;
  jobs: JobsSectionJob[];
  /** Optional count of parts linked to a job, keyed by job_id (ticket_jobs.job_id) */
  partsCountByJobId?: Record<string, number>;
  onAddPart?: () => void;
}

function formatMinutes(minutes: number): string {
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
}

export function JobsSection({
  ticketId,
  jobs,
  partsCountByJobId,
  onAddPart,
}: JobsSectionProps) {
  const [isAddJobOpen, setIsAddJobOpen] = useState(false);
  const [isAddLaborOpen, setIsAddLaborOpen] = useState(false);

  const laborEnabled = useFeatureFlag('labor_engine_enabled');
  const financialsEnabled = useExposureFlag('ui_ticket_financials');
  const showLabor = laborEnabled && financialsEnabled;

  const { role } = useUserRole();
  const canCompute = role === 'OWNER' || role === 'MANAGER' || role === 'SERVICE_WRITER';
  const isManagerView = role === 'OWNER' || role === 'MANAGER';

  // Sell-side labor lines (only fetched when financial flags are on).
  const { data: laborData } = useTicketLaborLines(ticketId, showLabor);
  const computeMut = useComputeJobLabor();

  // Phase 10 R3 — tech-pay lines (manager-only).
  const techPayExposure = useExposureFlag('ui_technician_pay');
  const showTechPay = laborEnabled && techPayExposure && isManagerView;
  const { data: techPayData } = useTicketTechPayLines(ticketId, showTechPay);
  const techPayByJobId = new Map<string, NonNullable<typeof techPayData>['lines'][number]>();
  for (const l of techPayData?.lines ?? []) techPayByJobId.set(l.job_id, l);
  const uncomputedTechPay = techPayData?.totals.uncomputed_count ?? 0;
  const computeTechPayMut = useComputeJobTechPay();

  // Map labor lines by ticket_jobs.job_id for per-row lookup.
  const laborByJobId = new Map<string, ReturnType<typeof toLaborMap>[number]>();
  function toLaborMap(lines: typeof laborData extends { lines: infer L } ? L : never) {
    return lines as any;
  }
  for (const l of laborData?.lines ?? []) {
    laborByJobId.set(l.job_id, l);
  }

  const totalMinutes = jobs.reduce((sum, j) => sum + (j.standardMinutes || 0), 0);
  const uncomputedCount = laborData?.totals.uncomputed_count ?? 0;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
            Jobs ({jobs.length})
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Total scheduled time: {formatMinutes(totalMinutes)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => setIsAddJobOpen(true)}
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add Job
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => setIsAddLaborOpen(true)}
          >
            <Timer className="h-3.5 w-3.5 mr-1" />
            Add Labor
          </Button>
          {onAddPart && (
            <Button
              variant="default"
              size="sm"
              className="h-8"
              onClick={onAddPart}
            >
              <Package2 className="h-3.5 w-3.5 mr-1" />
              Add Part
            </Button>
          )}
        </div>
      </div>

      {/* Bulk "Compute labor" link — null-only fill, never overwrites. */}
      {showLabor && uncomputedCount > 0 && canCompute && (
        <div className="flex items-center justify-between rounded-md border border-dashed bg-muted/20 px-3 py-1.5 text-xs">
          <span className="text-muted-foreground">
            {uncomputedCount} job{uncomputedCount === 1 ? '' : 's'} without labor computed
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            disabled={computeMut.isPending}
            onClick={() => computeMut.mutate({ ticketId })}
          >
            {computeMut.isPending && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
            Compute labor for all
          </Button>
        </div>
      )}

      {/* Phase 10 R3 — bulk "Compute tech pay" link — null-only fill, manager only. */}
      {showTechPay && uncomputedTechPay > 0 && (
        <div className="flex items-center justify-between rounded-md border border-dashed bg-muted/20 px-3 py-1.5 text-xs">
          <span className="text-muted-foreground">
            {uncomputedTechPay} job{uncomputedTechPay === 1 ? '' : 's'} without tech pay computed
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            disabled={computeTechPayMut.isPending}
            onClick={() => computeTechPayMut.mutate({ ticketId })}
          >
            {computeTechPayMut.isPending && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
            Compute tech pay for all
          </Button>
        </div>
      )}

      <div className="space-y-2">
        {jobs.length > 0 ? (
          jobs.map((job, index) => {
            const partsCount = partsCountByJobId?.[job.id] ?? 0;
            const laborLine = laborByJobId.get(job.id);
            return (
              <div
                key={job.id || index}
                className="bg-muted/30 rounded-lg p-3"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <Wrench className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">{job.name}</div>
                      {job.category && (
                        <div className="text-xs text-muted-foreground truncate">
                          {job.category}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {partsCount > 0 && (
                      <Badge variant="outline" className="text-[11px] gap-1">
                        <Package2 className="h-3 w-3" />
                        {partsCount}
                      </Badge>
                    )}
                    <Badge variant="secondary" className="font-mono text-xs gap-1">
                      <Clock className="h-3 w-3" />
                      {formatMinutes(job.standardMinutes)}
                    </Badge>
                  </div>
                </div>

                {/* Phase 10 R2 — sell-side labor strip (per row). */}
                {showLabor && laborLine && (
                  <JobLaborStrip ticketId={ticketId} line={laborLine} />
                )}

                {/* Phase 10 R3 — pay-side strip (manager-only). */}
                {showTechPay && techPayByJobId.get(job.id) && (
                  <JobTechPayStrip
                    ticketId={ticketId}
                    line={techPayByJobId.get(job.id)!}
                  />
                )}
              </div>
            );
          })
        ) : (
          <div className="text-center text-sm text-muted-foreground py-6 border border-dashed rounded-lg">
            No jobs on this ticket yet.
          </div>
        )}
      </div>

      {/* Add Job — reuses existing scheduler-safe path */}
      <AddJobDialog
        isOpen={isAddJobOpen}
        onClose={() => setIsAddJobOpen(false)}
        ticketId={ticketId}
      />

      {/* Add Labor — Phase 4C: standalone manual-labor job OR attached to existing job */}
      <AddLaborDialog
        isOpen={isAddLaborOpen}
        onClose={() => setIsAddLaborOpen(false)}
        ticketId={ticketId}
        existingJobs={jobs}
      />
    </div>
  );
}
